import React, { useState, useEffect } from "react";
import {
  CustomModal,
  ModalHeader,
  ModalBody,
  ModalActions,
} from "../../../../../base/components/CustomModal";
import Button from "../../../../../base/components/Button";
import { BUTTON_COLORS } from "../../../../../base/components/Button/appearance";
import { useService } from "../../../../../base/hooks/useService";
import UsersService from "../../../../../services/UsersService";
import ToasterService from "../../../../../services/ToastService";
import StepSelectTarget from "./StepSelectTarget";
import StepReview from "./StepReview";
import StepConfirm from "./StepConfirm";

export const SearchPlaceholder = () => <>No customer found.</>;

const MergeUsersModal = ({ isOpen, onClose, selectedUserInfo, onMergeSuccess }) => {
  const usersService = useService(UsersService);
  /**
   * @type {ToasterService}
   */
  const toastService = useService(ToasterService);

  const [step, setStep] = useState(0);
  const [selectedTargetUser, setSelectedTargetUser] = useState(null);
  const [reviewError, setReviewError] = useState("");
  const [mergeReason, setMergeReason] = useState("");
  const [questionnaireResolution, setQuestionnaireResolution] = useState(""); // "keepSource", "keepTarget", "latestSubmission"
  const [questionnaireConflict, setQuestionnaireConflict] = useState(false);
  const [shopifyPreference, setShopifyPreference] = useState(""); // "keepSource", "keepTarget"
  const [shopifyConflict, setShopifyConflict] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [sourceTestKits, setSourceTestKits] = useState([]);
  const [sourceLabs, setSourceLabs] = useState([]);
  const [targetTestKits, setTargetTestKits] = useState([]);
  const [targetLabs, setTargetLabs] = useState([]);
  const [sourceRecommendations, setSourceRecommendations] = useState([]);
  const [targetRecommendations, setTargetRecommendations] = useState([]);
  const [sourceQuestionnaires, setSourceQuestionnaires] = useState([]);
  const [targetQuestionnaires, setTargetQuestionnaires] = useState([]);
  const [shouldSwapSourceTarget, setShouldSwapSourceTarget] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setStep(0);
      setSelectedTargetUser(null);
      setReviewError("");
      setMergeReason("");
      setQuestionnaireResolution("");
      setQuestionnaireConflict(false);
      setShopifyPreference("");
      setShopifyConflict(false);
      setShouldSwapSourceTarget(false);
    }
  }, [isOpen]);

  const handleConfirmMerge = async () => {
    if (!selectedUserInfo?.id || !selectedTargetUser?.id || !mergeReason.trim())
      return;
    setConfirmLoading(true);
    try {
      let sourceUserId = selectedUserInfo.id;
      let targetUserId = selectedTargetUser.id;

      if (shouldSwapSourceTarget) {
        const tmp = targetUserId;
        targetUserId = sourceUserId;
        sourceUserId = tmp;
      }

      const payload = {
        sourceUserId,
        targetUserId,
        reason: mergeReason,
        ...(questionnaireResolution != null && questionnaireResolution !== ""
          ?  { questionnaireStrategy: questionnaireResolution }
          : {}),
      };

      await usersService.mergeUsers(payload);
      toastService.success("Users merged successfully.");
      onClose();
      if (onMergeSuccess) {
        onMergeSuccess();
      }
    } catch (err) {
      console.error("Merge users failed", err);
      toastService.error("Failed to merge users.");
    } finally {
      setConfirmLoading(false);
    }
  };

  const steps = [
    { title: "Select target" },
    { title: "Review" },
    { title: "Confirm" },
  ];

  const stepsHeadings = [
    { heading: "Select Target User" },
    { heading: "Review Merge Summary" },
    { heading: "Confirm Merge" },
  ];

  let stepBody = null;
  if (step === 0) {
    stepBody = (
      <StepSelectTarget
        selectedUserInfo={selectedUserInfo}
        selectedTargetUser={selectedTargetUser}
        setSelectedTargetUser={setSelectedTargetUser}
        usersService={usersService}
      />
    );
  } else if (step === 1) {
    stepBody = (
      <StepReview
        selectedUserInfo={selectedUserInfo}
        selectedTargetUser={selectedTargetUser}
        setShouldSwapSourceTarget={setShouldSwapSourceTarget}
        setReviewError={setReviewError}
        reviewError={reviewError}
        questionnaireResolution={questionnaireResolution}
        setQuestionnaireResolution={setQuestionnaireResolution}
        questionnaireConflict={questionnaireConflict}
        setQuestionnaireConflict={setQuestionnaireConflict}
        shopifyPreference={shopifyPreference}
        setShopifyPreference={setShopifyPreference}
        shopifyConflict={shopifyConflict}
        setShopifyConflict={setShopifyConflict}
        sourceTestKits={sourceTestKits}
        setSourceTestKits={setSourceTestKits}
        sourceLabs={sourceLabs}
        setSourceLabs={setSourceLabs}
        targetTestKits={targetTestKits}
        setTargetTestKits={setTargetTestKits}
        targetLabs={targetLabs}
        setTargetLabs={setTargetLabs}
        sourceRecommendations={sourceRecommendations}
        setSourceRecommendations={setSourceRecommendations}
        targetRecommendations={targetRecommendations}
        setTargetRecommendations={setTargetRecommendations}
        sourceQuestionnaires={sourceQuestionnaires}
        setSourceQuestionnaires={setSourceQuestionnaires}
        targetQuestionnaires={targetQuestionnaires}
        setTargetQuestionnaires={setTargetQuestionnaires}
      />
    );
  } else if (step === 2) {
    const mergedTestKits = [...sourceTestKits, ...targetTestKits];
    const mergedLabs = [...sourceLabs, ...targetLabs];
    const mergedRecommendations = [...sourceRecommendations, ...targetRecommendations];
    const mergedQuestionnaires = [...sourceQuestionnaires, ...targetQuestionnaires];
    stepBody = (
      <StepConfirm
        selectedTargetUser={shouldSwapSourceTarget ? selectedUserInfo : selectedTargetUser}
        mergeReason={mergeReason}
        labs={mergedLabs}
        recommendations={mergedRecommendations}
        questionnaires={mergedQuestionnaires}
        testKits={mergedTestKits}
      />
    );
  }

  return (
    <CustomModal isOpen={isOpen} className="merge-users-modal" size="lg">
      <ModalHeader onClose={onClose}>
        <>
        Merge Users
        <h5 className="my-2">{stepsHeadings[step].heading}</h5>
        <div className="d-flex align-items-center">
          {steps.map((s, idx) => (
            <span
              key={s.title}
              style={{
                marginRight: 8,
              }}
            >
              <span
                style={{
                  fontWeight: idx === step ? "bold" : "normal",
                  color: idx === step ? "#1976d2" : "#888",
                  marginRight: 8,
                  textDecoration: idx === step ? "underline" : "none",
                }}
              >
                {s.title}
              </span>
              {idx < steps.length - 1 && <span> &gt; </span>}
            </span>
          ))}
        </div></></ModalHeader>
      <ModalBody>
        <div style={{ minHeight: 300 }}>{stepBody}</div>
      </ModalBody>
      <ModalActions>
        {step === 1 && (
          <div className="flex-grow-1 d-flex align-items-center me-3">
            <label
              className="me-2 mb-0 fw-bold"
              htmlFor="merge-reason-input"
              style={{ whiteSpace: "nowrap" }}
            >
              Reason for merge:
            </label>
            <input
              id="merge-reason-input"
              type="text"
              className="form-control"
              style={{ minWidth: 320, maxWidth: 500 }}
              placeholder="Activated test kit from duplicate account"
              value={mergeReason}
              onChange={(e) => setMergeReason(e.target.value)}
            />
          </div>
        )}
        <Button
          color={BUTTON_COLORS.secondary}
          className="me-2"
          onClick={onClose}
        >
          Close
        </Button>
        {step > 0 && (
          <Button
            color={BUTTON_COLORS.secondary}
            className="me-2"
            onClick={() => setStep(step - 1)}
          >
            Back
          </Button>
        )}
        {step < steps.length - 1 ? (
          <Button
            color={BUTTON_COLORS.primary}
            onClick={() => setStep(step + 1)}
            disabled={
              (step === 0 && !selectedTargetUser) ||
              (step === 1 &&
                (!!reviewError ||
                  !mergeReason.trim() ||
                    (questionnaireConflict && !questionnaireResolution) ||
                    (shopifyConflict && !shopifyPreference)))
            }
          >
            Next
          </Button>
        ) : (
          <Button
            color={BUTTON_COLORS.primary}
            onClick={handleConfirmMerge}
            disabled={confirmLoading}
          >
            {confirmLoading ? "Merging..." : "Confirm Merge"}
          </Button>
        )}
      </ModalActions>
    </CustomModal>
  );
};

export default MergeUsersModal;
